import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Minus, ChevronDown, ChevronUp } from "lucide-react";

interface Product {
  _id: string;
  name?: string;
  price: number;
}

interface OrderQuantitySelectorProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  total: number;
  tipAmount?: number;
}

export function OrderQuantitySelector({
  products,
  quantities,
  onQuantityChange,
  total,
  tipAmount = 0,
}: OrderQuantitySelectorProps) {
  const [expanded, setExpanded] = useState(false);
  const displayTotal = total + tipAmount;

  return (
    <div className="space-y-4">
      <div className="-mx-4 my-4 border-y border-gray-200 bg-gray-100">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between py-4 px-4 text-left"
        >
          <span className="flex items-center gap-1 text-blue-600">
            Order summary
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
          <span className="font-bold text-black">${displayTotal.toFixed(2)}</span>
        </button>
        {expanded && (
          <div className="space-y-2 px-4 pb-4 pt-0">
            {products.map((product) => (
              <div
                key={product._id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {product.name || `Product ${product._id}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    ${product.price?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 border rounded-md">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentQty = quantities[product._id] || 0;
                        if (currentQty > 0) {
                          onQuantityChange(product._id, currentQty - 1);
                        }
                      }}
                      disabled={(quantities[product._id] || 0) === 0}
                      className="h-8 w-8"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[2rem] text-center font-medium">
                      {quantities[product._id] || 0}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentQty = quantities[product._id] || 0;
                        if (currentQty < 10) {
                          onQuantityChange(product._id, currentQty + 1);
                        }
                      }}
                      disabled={(quantities[product._id] || 0) >= 10}
                      className="h-8 w-8"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm px-4">
                <span>Tip</span>
                <span>${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold pt-2 border-t border-gray-300 px-4">
              <span>Total</span>
              <span>${displayTotal.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
